import { Signal, WritableSignal, signal } from '@angular/core';
import type { StorageScope, StorageToken } from '@typings/app/storage.types';

export class InMemoryStorage implements Storage {
  private readonly map = new Map<string, string>();

  get length(): number {
    return this.map.size;
  }

  clear(): void {
    this.map.clear();
  }

  getItem(key: string): string | null {
    return this.map.has(key) ? this.map.get(key)! : null;
  }

  key(index: number): string | null {
    if (index < 0) return null;
    const keys = [...this.map.keys()];
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.map.delete(key);
  }

  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
}

interface StorageEntry<T> {
  v: T; // value
  e?: number; // expiresAt epoch ms; omitted for non-TTL entries
}

type WatchEntry<S extends StorageScope> = {
  signal: WritableSignal<unknown>;
  token: StorageToken<unknown, S>;
};

export class StorageService<S extends StorageScope> {
  private readonly watched = new Map<string, WatchEntry<S>>();

  constructor(
    private readonly backend: Storage,
    private readonly scope: S,
  ) {
    if (this.scope === 'local' && typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorageEvent);
    }
  }

  get<T>(token: StorageToken<T, S> & { default: T }): T;
  get<T>(token: StorageToken<T, S>): T | null;
  get<T>(token: StorageToken<T, S>): T | null {
    const value = this.readFromBackend<T>(token);
    return value ?? token.default ?? null;
  }

  set<T>(token: StorageToken<T, S>, value: T): boolean {
    const entry: StorageEntry<T> = { v: value };
    if (token.ttl !== undefined) {
      entry.e = Date.now() + token.ttl;
    }
    try {
      this.backend.setItem(token.key, JSON.stringify(entry));
    } catch (err) {
      console.warn(`[storage] quota exceeded writing "${token.key}"`, err);
      return false;
    }
    this.watched.get(token.key)?.signal.set(value);
    return true;
  }

  remove(token: StorageToken<unknown, S>): void {
    this.backend.removeItem(token.key);
    const entry = this.watched.get(token.key);
    if (entry) entry.signal.set(entry.token.default ?? null);
  }

  clear(): void {
    this.backend.clear();
    for (const { signal, token } of this.watched.values()) {
      signal.set(token.default ?? null);
    }
  }

  watch<T>(token: StorageToken<T, S> & { default: T }): Signal<T>;
  watch<T>(token: StorageToken<T, S>): Signal<T | null>;
  watch<T>(token: StorageToken<T, S>): Signal<T | null> {
    const existing = this.watched.get(token.key);
    if (existing) return existing.signal as Signal<T | null>;

    const initial = this.get(token);
    const sig = signal<T | null>(initial);
    this.watched.set(token.key, {
      signal: sig as WritableSignal<unknown>,
      token: token as StorageToken<unknown, S>,
    });
    return sig;
  }

  private readFromBackend<T>(token: StorageToken<T, S>): T | null {
    const raw = this.backend.getItem(token.key);
    if (raw === null) return null;
    try {
      const entry = JSON.parse(raw) as StorageEntry<T>;
      if (entry.e !== undefined && entry.e <= Date.now()) {
        this.backend.removeItem(token.key);
        return null;
      }
      return entry.v;
    } catch {
      console.warn(`[storage] corrupt entry at "${token.key}"; removing`);
      this.backend.removeItem(token.key);
      return null;
    }
  }

  private onStorageEvent = (e: StorageEvent): void => {
    if (e.storageArea !== this.backend) return;

    if (e.key === null) {
      // another tab called clear()
      for (const { signal, token } of this.watched.values()) {
        signal.set(token.default ?? null);
      }
      return;
    }

    const entry = this.watched.get(e.key);
    if (!entry) return;

    if (e.newValue === null) {
      entry.signal.set(entry.token.default ?? null);
      return;
    }

    entry.signal.set(this.parseEventValue(e.newValue, entry.token));
  };

  private parseEventValue<T>(raw: string, token: StorageToken<T, S>): T | null {
    try {
      const parsed = JSON.parse(raw) as StorageEntry<T>;
      if (parsed.e !== undefined && parsed.e <= Date.now()) {
        return token.default ?? null;
      }
      return parsed.v;
    } catch {
      return token.default ?? null;
    }
  }
}
